import { apiSlice } from './apiSlice'

export type ReportsDashboard = {
  total_cases: number
  total_teams: number
  total_courtrooms: number
  submitted_scores_count: number
  average_total_score: number | null
  leaderboard: Array<{
    team_id: string
    team_name: string
    avg_score: number
    score_count: number
  }>
}

export type RoundCompletion = {
  case_id: string
  round_id: string
  expected_total: number
  submitted_count: number
  complete: boolean
  judges: Array<{
    judge_id: string
    judge_name: string
    judge_email?: string | null
    done: number
    expected: number
    complete: boolean
  }>
  teams: Array<{
    team_id: string
    team_name: string
    university?: string | null
  }>
}

export type VideoTiebreakBreakdown = {
  creativity: number
  presentation: number
  professionalism: number
}

export type RankingJudgeDetail = {
  judge_id: string
  judge_name: string
  judge_total: number
  submissions: Array<{
    submission_type: string  // 'memorial' | 'evidence' | 'transcript' | ''
    score: number
    score_id?: string | null
  }>
}

export type SubmissionJudgeBreakdown = {
  judge_id: string
  judge_name: string
  memorial_score: number
  evidence_score: number
  total: number
}

export type RankingRow = {
  rank: number
  team_id: string
  team_name: string
  university?: string | null
  judge_count: number
  score_count?: number  // tổng số phiếu (memorial + evidence + ...) tích lũy
  // ── Spec docs/operations/Dac_ta_Cham_diem_So_ket_video.md ──
  main_score?: number             // Điểm bài thi chính TB (Điều 10.2, max 100; R1 dual: max 200)
  main_score_total?: number       // Sum (chưa /num GK) — debug only
  main_score_max?: number         // 100 (legacy) | 200 (R1 dual)
  video_avg_score?: number        // Điểm video GK chấm TB (Điều 10.3, max 30)
  video_judge_count?: number      // số GK đã chấm video
  video_source?: 'judge_avg' | 'btc_manual' | 'none'
  effective_video_score?: number  // điểm video dùng để tie-break (= judge_avg HOẶC BTC fallback)
  // ── Spec PHẦN 6 — R1 dual_claimant_packets ──
  submission_1_score?: number
  submission_1_judges?: SubmissionJudgeBreakdown[]
  submission_1_judge_count?: number
  submission_1_representing_role?: string | null
  submission_2_score?: number
  submission_2_judges?: SubmissionJudgeBreakdown[]
  submission_2_judge_count?: number
  submission_2_representing_role?: string | null
  // ── Legacy (giữ backward-compat) ──
  average_score: number  // = main_score
  total_score: number    // = main_score_total (R1 dual: = main_score)
  judge_details?: RankingJudgeDetail[]  // breakdown per-judge (legacy single)
  video_tiebreak_total?: number  // BTC chấm tay (legacy)
  video_tiebreak?: VideoTiebreakBreakdown  // BTC chấm tay (legacy)
  tied_with_neighbor?: boolean
  // S-1 fix (audit 2026-05-22): true khi chưa đủ phiếu (sub1/sub2 thiếu GK).
  incomplete?: boolean
  submission_1_incomplete?: boolean
  submission_2_incomplete?: boolean
}

export type RankingResult = {
  case_id: string
  round_id: string | null
  total_teams: number
  ranking: RankingRow[]
  tie_break_applied?: boolean
  flow?: 'dual_claimant_packets' | 'legacy_single_role'  // PHẦN 6 — chỉ trả khi R1 dual
  main_score_max?: number  // 200 với dual; undefined với legacy (UI auto-fallback 100)
  video_tiebreak_max?: number  // 30 với dual
}

export type NextRoundSchedule = {
  bracket_at?: string
  role_sheet_due?: string
  hearing_at?: string
}

export type PublishRoundBody = {
  case_id: string
  round_id: string
  advancing_team_ids: string[]
  title?: string
  note?: string
  next_round_label?: string
  next_round_schedule?: NextRoundSchedule
  force?: boolean
}

export type PublishRoundResult = {
  published_id: string
  case_id: string
  round_id: string
  advancing_team_ids: string[]
  notified_teams: number
  ranking: RankingRow[]
  idempotent?: boolean  // BE trả về true nếu là duplicate call trong 5 phút
}

export const reportsApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    // C2 (audit 2026-05-22): providesTags['Score'] để Reports tự invalidate
    // khi scoring mutation (create/update/submit/unlock/reopen) fire → admin
    // không cần reload trang để thấy điểm mới.
    getReportsDashboard: builder.query<ReportsDashboard, void>({
      query: () => '/reports/dashboard',
      providesTags: ['Score'],
    }),
    exportScoresCsv: builder.query<Blob, void>({
      query: () => ({
        url: '/reports/export/scores.csv',
        responseHandler: (response: Response) => response.blob(),
      }),
    }),
    exportSummaryTxt: builder.query<string, void>({
      query: () => ({
        url: '/reports/export/summary.txt',
        responseHandler: (response: Response) => response.text(),
      }),
    }),
    exportSummaryPdf: builder.query<Blob, void>({
      query: () => ({
        url: '/reports/export/summary.pdf',
        responseHandler: (response: Response) => response.blob(),
      }),
    }),
    // UAT-TC #15: kiểm tra giám khảo đã chấm xong
    getRoundCompletion: builder.query<RoundCompletion, { case_id: string; round_id: string }>({
      query: ({ case_id, round_id }) => ({
        url: '/reports/round-completion',
        params: { case_id, round_id },
      }),
      providesTags: ['Score'],
    }),
    // UAT-TC #15 + Chung kết #16: ranking theo vòng (round_id=null → tổng hợp)
    getRanking: builder.query<RankingResult, { case_id: string; round_id?: string | null }>({
      query: ({ case_id, round_id }) => ({
        url: '/reports/ranking',
        params: round_id ? { case_id, round_id } : { case_id },
      }),
      providesTags: ['Score'],
    }),
    // UAT-TC #16 + Bán kết #11 + Chung kết #18: công bố kết quả
    publishRound: builder.mutation<PublishRoundResult, PublishRoundBody>({
      query: (body) => ({
        url: '/reports/publish-round',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Score', 'Team'],
    }),
    // C3-a (audit 2026-05-22): R1 shortlist — admin bulk set team status.
    setR1Shortlist: builder.mutation<
      {
        case_id: string
        shortlisted_count: number
        eliminated_count: number
        shortlisted_team_ids: string[]
      },
      { case_id: string; shortlisted_team_ids: string[] }
    >({
      query: (body) => ({
        url: '/reports/r1-shortlist',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Team', 'Score'],
    }),
    // Tie-break Điều 10.3 — chấm Video clip
    setVideoTiebreak: builder.mutation<
      any,
      { team_id: string; creativity: number; presentation: number; professionalism: number }
    >({
      query: ({ team_id, ...body }) => ({
        url: `/teams/${team_id}/video-tiebreak`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: (res, err, { team_id }) => [
        { type: 'Team', id: team_id },
        'Team',
      ],
    }),
    // Public Guest endpoints
    getPublicResultsCases: builder.query<
      Array<{ case_id: string; title: string; case_type?: string | null }>,
      void
    >({
      query: () => '/public/results/cases',
    }),
    getPublicLatestResults: builder.query<
      {
        case_id: string
        case_title: string
        round: { round_name: string; round_type: string } | null
        published_at: string
        title: string
        note: string
        total_teams: number
        advancing_count: number
        advancing_teams: Array<{
          team_name: string
          university: string
          competition_status: string | null
        }>
      },
      string
    >({
      query: (caseId) => `/public/results/${caseId}/latest`,
    }),
    getPublicVerdicts: builder.query<
      Array<{
        courtroom_id: string
        round_name: string | null
        round_type: string | null
        verdict_summary: string | null
        verdict_legal_basis: string | null
        verdict_penalty: string | null
        winner_team: { team_id: string; team_name: string } | null
        published_at: string | null
      }>,
      string
    >({
      query: (caseId) => `/public/verdicts/${caseId}`,
    }),
    getPublicSchedule: builder.query<
      {
        case_id: string
        case_title: string
        rounds: Array<{
          round_id: string
          round_name: string
          round_type: string
          scheduled_date: string | null
          courtroom_started_at: string | null
          matches: Array<{
            team_plaintiff: { name: string; university: string } | null
            team_defendant: { name: string; university: string } | null
            // P0+++ RBAC audit 2026-05-20: bỏ courtroom_id khỏi public schedule
            // (chặn enum chain → VOD leak). UI dùng livestream_enabled để show
            // nút "Xem trực tiếp" nếu cần.
            livestream_enabled: boolean
            started_at: string | null
          }>
        }>
      },
      string
    >({
      query: (caseId) => `/public/schedule/${caseId}`,
    }),
  }),
})

export const {
  useGetReportsDashboardQuery,
  useLazyExportScoresCsvQuery,
  useLazyExportSummaryTxtQuery,
  useLazyExportSummaryPdfQuery,
  useGetRoundCompletionQuery,
  useLazyGetRoundCompletionQuery,
  useGetRankingQuery,
  useLazyGetRankingQuery,
  usePublishRoundMutation,
  useSetR1ShortlistMutation,
  useSetVideoTiebreakMutation,
  useGetPublicResultsCasesQuery,
  useGetPublicLatestResultsQuery,
  useGetPublicVerdictsQuery,
  useGetPublicScheduleQuery,
} = reportsApi
